import React, { FC, FormEvent, useEffect } from 'react';

import { Box, SxProps, Theme } from '@mui/system';
import { IconButton, Typography } from '@mui/material';
import {
  useStripe,
  useElements,
  PaymentElement as StripePayment,
} from '@stripe/react-stripe-js';
import { Close } from '@mui/icons-material';

import {
  getIntentSecret,
  intentSecret,
  istermsAgreed,
  istermsAgreedStatus,
  PaymentPlans,
  paymentResetValue,
  pharmacyBillingSelector,
  setPaymentLoad,
} from '@pharmaplan/common';

import usePaymentRedirectSuccess from '../../../hooks/Payment/usePaymentRedirectSuccess';
import useProfileFormDailog from '../../../hooks/ProfileForm/useProfileFormDailog';
import usePaymentComponent from '../../../hooks/Payment/usePaymentComponent';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../hooks/useAppSelector';
import { resetDialog } from '../../../reducers/dialogReducer';
import CustomCheckbox from '../../common/CustomCheckbox';
import CustomButton from '../../common/CustomButton';
import { showPaymentError } from '../PaymentGateway';
import strings from '../../../localization';

import styles from './styles';

interface IPaymentComponent {
  paymentPlan: PaymentPlans;
  showFees?: boolean;
  showTerms?: boolean;
  customTitle?: string;
  customSubmitLabel?: string;
  customContainer?: SxProps<Theme>;
  isDialog?: boolean;
}

const { basic, unlimited } = PaymentPlans;

const PaymentComponent: FC<IPaymentComponent> = ({
  paymentPlan,
  showFees,
  showTerms,
  customSubmitLabel,
  customTitle,
  customContainer,
  isDialog,
}) => {
  const dispatch = useAppDispatch();

  const { showTermsAndConditions } = useProfileFormDailog();
  const termsAgreed = useAppSelector(istermsAgreedStatus);
  const billing = useAppSelector(pharmacyBillingSelector);
  const clientSecret = useAppSelector(intentSecret);
  const { unlimitedFees, basicFees } = billing ?? {};

  const stripe = useStripe();
  const elements = useElements();

  const { paymentConfig } = usePaymentComponent(paymentPlan);

  usePaymentRedirectSuccess();

  const feesConfig = {
    [basic]: {
      perContract: `${basicFees} + ${strings.tax}`,
      payNow: 0,
      buttonLabel: strings.submit,
    },
    [unlimited]: {
      perContract: 0,
      payNow: `${unlimitedFees} + ${strings.tax}`,
      buttonLabel: strings.submitAndPay,
    },
  };

  const { payNow, perContract, buttonLabel } = feesConfig[paymentPlan] ?? {};

  const metadata = [
    {
      key: 1,
      label: strings.perContractFee,
      value: `$${perContract}`,
    },
    {
      key: 2,
      label: strings.payNow,
      value: `$${payNow}`,
    },
  ];

  const options = {
    business: { name: strings.pharmagap },
    defaultValues: {
      billingDetails: {
        address: {
          country: 'CA',
        },
      },
    },
    terms: { card: 'never' as const },
    fields: {
      billingDetails: {
        address: {
          postalCode: 'auto' as const,
          country: 'never' as const,
        },
      },
    },
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // CHECK IF STRIPE HAS LOADED
    if (!stripe || !elements) return;

    // VALIDATE PAYMENT FORMAT IS CORRECT
    const { error: submitError } = (await elements.submit()) ?? {};

    if (submitError) {
      showPaymentError(dispatch, submitError.message ?? '');
      return;
    }

    // GET SECRET BASED ON PAYMENT PLAN
    await dispatch(getIntentSecret(paymentPlan));
  };

  const handleCheckboxClick = () => {
    dispatch(istermsAgreed(!termsAgreed));
  };

  useEffect(() => {
    if (clientSecret) {
      dispatch(setPaymentLoad(true));
      paymentConfig?.[paymentPlan].confirmStripePayment(clientSecret);
      dispatch(paymentResetValue(['clientSecret']));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientSecret]);

  const onClickClose = () => {
    dispatch(resetDialog());
  };

  return (
    <Box
      onSubmit={handleSubmit}
      id="payment-form"
      component="form"
      sx={[styles.container, customContainer] as SxProps<Theme>}
    >
      {isDialog && (
        <Box sx={styles.closeContainer}>
          <IconButton size="small" sx={styles.closeIcon} onClick={onClickClose}>
            <Close />
          </IconButton>
        </Box>
      )}
      <Typography sx={styles.title}>
        {customTitle ?? strings.billingInformation}
      </Typography>

      <StripePayment id="payment-element" options={options} />

      {showFees && (
        <Box sx={styles.metadataContainer}>
          {metadata.map((item, index) => {
            const { key, label, value } = item;

            const isLast = index === metadata.length - 1;

            return (
              <Box sx={styles.textContainer} key={key}>
                <Typography sx={styles.label}>{label}</Typography>

                <Box sx={styles.labelContainer}>
                  <Typography
                    sx={[styles.label, styles.value, isLast && styles.total]}
                  >
                    {value}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Box>
      )}

      {showTerms && (
        <CustomCheckbox
          label={strings.agreeOn}
          linkLabel={strings.termsAndConditions}
          name="termsAndConditions"
          handleChange={handleCheckboxClick}
          value={termsAgreed}
          id="termsAndConditions"
          onLinkClick={showTermsAndConditions}
          labelClass={styles.checkboxLabel}
          customClass={styles.checkboxContainer}
        />
      )}
      <CustomButton
        customClass={styles.buttonContainer}
        label={customSubmitLabel ?? buttonLabel}
        type="submit"
        disabled={!termsAgreed && showTerms}
      />
    </Box>
  );
};

export default PaymentComponent;
